import React from "react";
import { useAnketaI18n } from "src/internationalization";

/*
 * "Certificate expiry sorting" - ported from the product presentation. opsanio
 * collects every certificate and groups them by remaining runtime: kritisch
 * (≤14d), Warnung (15-90d), sicher (>90d). HTML/flex (not SVG) since it's a card
 * layout. Rendered on the navy stage; severity colors via --ak-sev-* tokens.
 */

type Col = { key: string; titleFb: string; range: string; color: string; rows: Array<[string, string, string]> };

const COLS: Col[] = [
  {
    key: "critical",
    titleFb: "Kritisch",
    range: "≤ 14 Tage",
    color: "var(--ak-sev-critical)",
    rows: [
      ["proxy.kunde.de", "Load Balancer", "3 T"],
      ["*.kunde.de", "Wildcard", "8 T"],
      ["api.kunde.de", "API-Gateway", "11 T"],
      ["mail-gw.kunde.de", "Mail-Relay", "14 T"],
    ],
  },
  {
    key: "warning",
    titleFb: "Warnung",
    range: "15 - 90 Tage",
    color: "var(--ak-sev-warning)",
    rows: [
      ["mail.kunde.de", "Exchange", "21 T"],
      ["db.kunde.de", "Datenbank", "45 T"],
      ["vpn.kunde.de", "VPN-Gateway", "54 T"],
      ["portal-stg.kunde.de", "Staging", "80 T"],
    ],
  },
  {
    key: "ok",
    titleFb: "Sicher",
    range: "> 90 Tage",
    color: "var(--ak-sev-ok)",
    rows: [
      ["portal.kunde.de", "Kundenportal", "180 T"],
      ["backup.kunde.de", "Backup", "240 T"],
      ["intern.kunde.local", "Interne CA", "312 T"],
      ["ldap.kunde.de", "Verzeichnis", "365 T"],
    ],
  },
];

export const AutonubilCertSorting: React.FC = () => {
  const { translate } = useAnketaI18n();
  const tp = "anketa.autonubil.home.certs";
  const t = (k: string, d: string) => translate(`${tp}.${k}`, { defaultMessage: d } as any);

  return (
    <div
      style={{
        width: "100%",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
        gap: 18,
      }}
      role="img"
      aria-label={t(
        "aria",
        "opsanio collects all certificates and groups them by remaining runtime: critical (14 days or less), warning (15 to 90 days) and safe (over 90 days).",
      )}
    >
      {COLS.map((c, ci) => (
        <div
          key={c.key}
          style={{
            borderRadius: 16,
            padding: 16,
            border: `1px dashed ${c.color}`,
            background: "rgba(255,255,255,0.02)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: c.color,
              fontSize: "0.8rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            <span>{t(`${c.key}.title`, c.titleFb)}</span>
            <span style={{ fontSize: "1.4rem", fontWeight: 800 }}>{c.rows.length}</span>
          </div>
          <div style={{ color: "var(--ak-on-navy-muted)", fontSize: "0.7rem", margin: "2px 0 12px" }}>
            {t(`${c.key}.range`, c.range)}
          </div>
          {c.rows.map((r, ri) => (
            <div
              key={r[0]}
              className="ak-sort-in"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                background: "var(--ak-on-navy-surface)",
                border: "1px solid var(--ak-on-navy-border)",
                borderRadius: 10,
                padding: "9px 12px",
                marginBottom: 8,
                animationDelay: `${ci * 0.09 + ri * 0.07}s`,
              }}
            >
              <span
                className={c.key === "critical" ? "ak-dot-pulse" : undefined}
                style={{ width: 8, height: 8, borderRadius: "50%", background: c.color, flex: "none" }}
              />
              <div style={{ minWidth: 0 }}>
                <div style={{ color: "var(--ak-on-navy)", fontWeight: 600, fontSize: "0.82rem", lineHeight: 1.1 }}>{r[0]}</div>
                <div style={{ color: "var(--ak-on-navy-muted)", fontSize: "0.68rem" }}>{r[1]}</div>
              </div>
              <span style={{ marginLeft: "auto", color: c.color, fontWeight: 700, fontSize: "0.8rem" }}>{r[2]}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export default AutonubilCertSorting;
